<template>
  <section class="main">
    <h1 class="intro">{{ $t("MYAPPSTORE_HEADING") }}</h1>
    <div class="row">
      <div>
        <p class="g-text">
          {{ $t("MYAPPSTORE_DESC") }}
          <b class="email"
            >Email ID:
            {{ $store.getters.devices[0] && $store.getters.devices[0].email }}
          </b>
        </p>
        <div class="header">
          <h2 class="header__title">{{ $t("YOUR APPS") }}</h2>
          <span class="header__storage"
            >{{ $t("STORAGE USAGE") }}: {{ totalSize }} / 5 GB</span
          >
        </div>
        <app
          v-for="app in apps"
          :app="app"
          @update="getApps"
          :key="app.id"
          class="main__app"
          :isInStore="true"
        />
      </div>
      <div class="upload">
        <form class="upload__box">
          <input
            @change="newFilesHandler"
            class="upload__input"
            ref="upload"
            type="file"
            multiple
          />
          <button @click.prevent="select" class="upload__button">
            {{ $t("UPLOAD_IPA_FILES") }}
          </button>
          <span v-if="uploads.length" class="upload__num">
            {{ $t("FILES_SELECTED", { count: uploads.length }) }}
          </span>
          <ul>
            <li v-for="upload in uploads" :key="upload.id" class="upload__item">
              <template v-if="!upload.status">
                <span class="upload__title">{{ upload.name }}</span>
                <span class="upload__progress-wrapper">
                  <span class="upload__progress">
                    <span
                      :style="{ width: upload.progress + '%' }"
                      class="upload__progress-inner"
                    ></span>
                  </span>
                  {{ upload.progress }}%
                </span>
              </template>
              <template v-else>
                <span
                  class="upload__status"
                  :class="{ upload__status_error: upload.error }"
                >
                  {{ upload.status }}
                </span>
                <span class="upload__date">
                  {{ upload.date }}
                  <img
                    v-if="upload.finished"
                    @click="deleteJob(upload.id)"
                    class="upload__delete"
                    src="@/assets/icon-delete.svg"
                  />
                </span>
              </template>
            </li>
          </ul>
        </form>
        <p class="help g-text">
          <img class="help__icon" src="@/assets/icon-info.svg" />
          <html-with-links
            tag="span"
            class="help__text"
            :html="
              $t('CONFIGURE_FEATURES_AT_PAGE', {
                configureLink: '/my/configure',
              })
            "
          ></html-with-links>
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import App from '@/components/App';
import prettysize from 'prettysize';
import HtmlWithLinks from '@/components/HtmlWithLinks';

export default {
  components: { App, HtmlWithLinks },
  data() {
    return {
      apps: [],
      uploads: [],
      interval: null,
      interval2: null,
    };
  },
  computed: {
    title() {
      return this.$t('MYAPPSTORE_HEADING');
    },
    totalSize() {
      return prettysize(this.apps.reduce((total, a) => total + (+a.size), 0));
    },
  },
  methods: {
    select() {
      this.$refs.upload.click();
    },
    newFilesHandler() {
      Array.from(this.$refs.upload.files).forEach((file) => {
        const id = [...Array(40)].map(() => Math.random().toString(36)[2]).join('');
        const app = {
          id,
          name: file.name,
          progress: 0,
          status: '',
          date: '',
          finished: false,
          error: false,
        };
        this.uploads.unshift(app);
        this.$api.uploadApp({ file, id }, (e) => {
          app.progress = Math.round((e.loaded * 100) / e.total);
        }).then((response) => {
          if (!response.success) {
            app.status = this.$t('UPLOAD_FAILED_WITH_REASON', { reason: response.errors[0].translated });
            app.error = true;
          } else {
            app.status = this.$t('REQUESTING_VALIDATION');
          }
        }).catch(() => {
          app.status = this.$t('UPLOAD_FAILED');
          app.error = true;
        });
      });
    },
    getApps() {
      this.$api.getIpas().then((apps) => {
        this.apps = apps;
      });
    },
    deleteJob(id) {
      this.$api.deleteAppJob(id).then(() => {
        const index = this.uploads.findIndex(u => u.id === id);
        this.uploads.splice(index, 1);
      });
    },
    getJobs() {
      this.$api.getAppsJobs().then((jobs) => {
        jobs.forEach((newJob) => {
          const oldJob = this.uploads.find(u => u.id === newJob.id);
          const date = new Date(newJob.updated_at * 1000).toLocaleString();
          const error = /error/i.test(newJob.status);

          if (!oldJob) {
            this.uploads.push({
              id: newJob.id, status: newJob.status, date, finished: newJob.is_finished, error,
            });
            return;
          }

          oldJob.status = newJob.status;
          oldJob.date = date;
          oldJob.error = error;
          if (newJob.is_finished && !oldJob.finished) {
            oldJob.finished = true;
            this.getApps();
          }
        });
      });
    },
  },
  mounted() {
    this.getApps();
    this.getJobs();
    this.interval = setInterval(this.getJobs, 1000);
    this.interval2 = setInterval(this.getApps, 5000);
  },
  destroyed() {
    clearInterval(this.interval);
    clearInterval(this.interval2);
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: block;
  flex-grow: 1;

  &__app {
    padding: 10px 0;
    border-bottom: 1px solid #e5e5e5;
  }
}

.email {
  display: block;
  font-weight: 600;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e5e5;
  margin-top: 20px;

  &__title {
    color: $text-primary;
    font-weight: 600;
    font-size: 16px;
  }

  &__storage {
    background: $lighter-primary;
    border-radius: 2px;
    padding: 3px;
  }
}

.row {
  display: flex;

  @include notebook {
    flex-direction: column-reverse;
  }
}

.upload {
  flex: 0 0 200px;
  margin-left: 40px;

  @include notebook {
    margin: 0 auto 20px;
  }

  &__input {
    opacity: 0;
    position: absolute;
    z-index: -1;
  }

  &__box {
    background: #ffffff;
    border: 1px solid #ebebeb;
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    padding: 15px;
  }

  &__button {
    display: block;
    background: $primary;
    border-radius: 2px;
    color: #fff;
    font-size: 14px;
    padding: 7px;
    width: 100%;
  }

  &__num {
    display: block;
    text-align: center;
    font-size: 12px;
    margin: 10px 0;
  }

  &__item {
    border-top: 1px solid #e5e5e5;
    padding: 10px 0;

    &:last-child {
      padding-bottom: 0;
    }
  }

  &__title {
    font-size: 12px;
    font-weight: 500;
    word-break: break-word;
  }

  &__progress-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $text-secondary;
    font-size: 12px;
    margin-top: 4px;
  }

  &__progress {
    flex: 0 0 130px;
    height: 3px;
    position: relative;
    background: #d8d8d8;
    border-radius: 2px;

    &-inner {
      height: 3px;
      position: absolute;
      background: $primary;
      border-radius: 2px;
      width: 0;
    }
  }

  &__status {
    display: block;
    word-break: break-word;
    background: #d7f4da;
    border-radius: 2px;
    color: #3c4f3e;
    font-size: 12px;
    padding: 3px;
    margin: 6px 0;

    &_error {
      background: #fddee6;
    }
  }

  &__date {
    display: flex;
    justify-content: space-between;
    color: $text-secondary;
    font-size: 10px;
  }

  &__delete {
    margin-left: 5px;
    cursor: pointer;
  }
}

.help {
  font-size: 12px;
  margin: 10px 10px 0;
  padding-top: 10px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  align-items: flex-start;

  &__icon {
    margin-top: 3px;
    margin-right: 5px;
  }
}
</style>
